import { useEffectWithoutCleanup, useState } from "Shared/haunted/CustomHooks";
import { DynamicPromoCodeSetting, DynamicSettings } from "./DynamicSettings";
import classNames from "classnames";
import { useSaveDynamicSettings } from "./useSaveDynamicSettings";
import { html } from "lit-html";

export interface Props {
	newSettings: DynamicSettings;
	setNewSettings: (settings: DynamicSettings) => void;
}

interface HandleInputParams {
	e: InputEvent;
	setting: DynamicPromoCodeSetting;
	key: keyof DynamicPromoCodeSetting;
	index: number;
}

export const usePromoCodeSettings = (props: Props) => {
	const [isValidated, setIsValidated] = useState(false);

	const validate = () => {
		setIsValidated(true);
		return props.newSettings.DynamicPromoCodeSettings.filter(
			(setting) => setting.Code || setting.Culture || setting.ProgramCodeAndLevel || setting.RoleCode
		).every((setting) => setting.Code && (setting.Culture || setting.RoleCode || setting.ProgramCodeAndLevel));
	};

	const saver = useSaveDynamicSettings({ newSettings: props.newSettings, validate });

	const addEmpty = () => {
		props.setNewSettings({
			...props.newSettings,
			DynamicPromoCodeSettings: [
				...props.newSettings.DynamicPromoCodeSettings,
				{
					Code: "",
					Culture: "",
					RoleCode: "",
					ProgramCodeAndLevel: "",
				},
			],
		});
	};

	const deleteRow = (setting: DynamicPromoCodeSetting) => {
		props.setNewSettings({
			...props.newSettings,
			DynamicPromoCodeSettings: props.newSettings.DynamicPromoCodeSettings.filter(
				(s) =>
					s.Code !== setting.Code ||
					s.Culture !== setting.Culture ||
					s.RoleCode !== setting.RoleCode ||
					s.ProgramCodeAndLevel !== setting.ProgramCodeAndLevel
			),
		});
	};

	const handleInput = (params: HandleInputParams) => {
		const { e, setting, key, index } = params;
		const value = (e.target as HTMLInputElement).value;
		const oldSetting = props.newSettings.DynamicPromoCodeSettings[index];
		const newPromoCodeSettings = oldSetting
			? props.newSettings.DynamicPromoCodeSettings.map((newSetting, i) =>
					i === index ? { ...newSetting, [key]: value } : newSetting
				)
			: [...props.newSettings.DynamicPromoCodeSettings, { ...setting, [key]: value }];
		props.setNewSettings({ ...props.newSettings, DynamicPromoCodeSettings: newPromoCodeSettings });
	};

	useEffectWithoutCleanup(addEmpty, []);

	const cultureTemplate = (promoCode: DynamicPromoCodeSetting, index: number) => {
		return html`
			<td class="px-2 py-4 align-bottom">
				<label class="mb-1 block text-sm">Culture</label>
				<input
					class=${classNames(
						"flex h-10 w-full appearance-none items-center rounded-lg border border-solid border-black px-4",
						{
							"bg-n-red/30":
								isValidated &&
								!promoCode.ProgramCodeAndLevel &&
								!promoCode.Culture &&
								!promoCode.RoleCode &&
								promoCode.Code,
						}
					)}
					value=${promoCode.Culture || ""}
					@input=${(e: InputEvent) => handleInput({ e, setting: promoCode, key: "Culture", index })}
				/>
			</td>
		`;
	};

	const roleCodeTemplate = (promoCode: DynamicPromoCodeSetting, index: number) => {
		return html`
			<td class="px-2 py-4 align-bottom">
				<label class="mb-1 block text-sm">Role Code</label>
				<input
					class=${classNames(
						"flex h-10 w-full appearance-none items-center rounded-lg border border-solid border-black px-4",
						{
							"bg-n-red/30":
								isValidated &&
								!promoCode.ProgramCodeAndLevel &&
								!promoCode.Culture &&
								!promoCode.RoleCode &&
								promoCode.Code,
						}
					)}
					value=${promoCode.RoleCode || ""}
					@input=${(e: InputEvent) => handleInput({ e, setting: promoCode, key: "RoleCode", index })}
				/>
			</td>
		`;
	};

	const programLevelTemplate = (promoCode: DynamicPromoCodeSetting, index: number) => {
		return html`
			<td class="px-2 py-4 align-bottom">
				<label class="mb-1 block text-sm">Prg Code-Level (BEC-SMT)</label>
				<input
					class=${classNames(
						"flex h-10 w-full appearance-none items-center rounded-lg border border-solid border-black px-4",
						{
							"bg-n-red/30":
								isValidated &&
								!promoCode.ProgramCodeAndLevel &&
								!promoCode.Culture &&
								!promoCode.RoleCode &&
								promoCode.Code,
						}
					)}
					value=${promoCode.ProgramCodeAndLevel || ""}
					@input=${(e: InputEvent) => handleInput({ e, setting: promoCode, key: "ProgramCodeAndLevel", index })}
				/>
			</td>
		`;
	};

	const codeTemplate = (promoCode: DynamicPromoCodeSetting, index: number) => {
		return html`
			<td class="px-2 py-4 align-bottom">
				<label class="mb-1 block text-sm">Navitaire Promo Code</label>
				<input
					class=${classNames(
						"flex h-10 w-full appearance-none items-center rounded-lg border border-solid border-black px-4",
						{
							"bg-n-red/30":
								isValidated &&
								!promoCode.Code &&
								(promoCode.Culture || promoCode.RoleCode || promoCode.ProgramCodeAndLevel),
						}
					)}
					value=${promoCode.Code || ""}
					@input=${(e: InputEvent) => handleInput({ e, setting: promoCode, key: "Code", index })}
				/>
			</td>
		`;
	};

	const rowTemplate = (promoCode: DynamicPromoCodeSetting, index: number) => html`
		<tr class="border-b border-solid border-n-blue pb-4 odd:bg-gray-100">
			${cultureTemplate(promoCode, index)} ${roleCodeTemplate(promoCode, index)}
			${programLevelTemplate(promoCode, index)} ${codeTemplate(promoCode, index)}
			${buttonsTemplate(promoCode, index)}
		</tr>
	`;

	const buttonsTemplate = (promoCode: DynamicPromoCodeSetting, index: number) => html`
		<div class="flex h-full w-full flex-col items-center justify-center px-2 pb-2 pt-[52px] text-center">
			<label class="mb-1 block text-sm"></label>
			${deleteButtonTemplate(promoCode, index)} ${addButtonTemplate(index)}
		</div>
	`;

	const deleteButtonTemplate = (promoCode: DynamicPromoCodeSetting, index: number) =>
		index < props.newSettings.DynamicPromoCodeSettings.length - 1
			? html`
					<div
						class="cursor-pointer rounded-lg bg-n-red px-2 py-1 text-xs font-bold text-white hover:bg-n-blue hover:text-white"
						@click=${() => deleteRow(promoCode)}
					>
						Delete
					</div>
				`
			: "";

	const addButtonTemplate = (index: number) =>
		index === props.newSettings.DynamicPromoCodeSettings.length - 1
			? html`
					<div
						class="cursor-pointer rounded-lg bg-n-orange px-2 py-1 text-xs font-bold text-white hover:bg-n-blue hover:text-white"
						@click=${addEmpty}
					>
						Add
					</div>
				`
			: "";

	const htmlTemplate = () => html`
		<section class="mt-6 max-w-[640px]">
			<table>
				${props.newSettings.DynamicPromoCodeSettings.map(rowTemplate)}
			</table>

			${saver.htmlTemplate()}
		</section>
	`;

	return { htmlTemplate };
};
